/** @prettier */
import React, { Fragment } from 'react';

import StyledBetmatchSpan from '../../betmatch/components/StyledSpan/StyledSpan';
import StyledEasySpan from '../../easybet/components/StyledEasySpan/StyledEasySpan';
import StyledMatchbookSpan from '../../matchbook/components/StyledSpan';
import StyledWagyrSpan from '../../wagyr/components/StyledSpan/StyledSpan';

import SettingsIconEasy from '../../easybet/assets/icons/settings.svg';

import GbFlagIcon from '../../../images/flags/gb.svg';
import MxFlagIcon from '../../../images/flags/mx.svg';
import BetmatchLogoDark from '../../betmatch/assets/images/logo_Betmatch_dark.svg';
import BetmatchLogoLight from '../../betmatch/assets/images/logo_Betmatch_light.svg';
import MatchbookLogoName from '../../matchbook/assets/images/logos/matchbook-logo-light.svg';
import MatchbookLogo from '../../matchbook/assets/images/logos/matchbook-logo.svg';

import EasyLogo from '../../easybet/assets/icons/logo_easyBet.svg';
import EasyLogoShort from '../../easybet/assets/icons/logo_easyBet_short.svg';
import SearchIconEasy from '../../easybet/assets/icons/search.svg';

const noop = () => null;

/** Section A - getClient... functions */
export const getClientAppBG = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return '/assets/images/BG-Image.jpg';
    case 'easybet-net':
    case 'easybet-predictions':
      return '/assets/images/BG-Image.jpg';
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      return '/assets/images/BG-Image.jpg';
    case 'wagyr-io':
      return '/assets/images/BG-Image-inverted.jpg';
    case 'blueprint-net':
      return '/assets/images/BG-Image.jpg';
    default:
      return null;
  }
};

export const getClientDomain = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return 'betmatch.co.za';
    case 'easybet-net':
      return 'easybet.net';
    case 'easybet-predictions':
      return 'predictions.easybet.net';
    case 'matchbook-com':
    case 'matchbook-net': // this is a safeguard due to the default site value
      return 'matchbook.com';
    case 'matchbook-mx':
      return 'matchbook.mx';
    case 'matchbook-predictions':
      return 'predictstreet.matchbook.com';
    case 'matchbook-predictions-mexico':
      return 'predictstreet.matchbook.mx';
    case 'matchbook-erik':
      // TODO: this is a temporary domain
      return 'erik.matchbook.com';
    case 'wagyr-io':
      return 'wagyr.io';
    case 'blueprint-net':
      return 'blueprint.net';
    default:
      return null;
  }
};

export const getClientEmail = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return 'support@betmatch.co.za';
    case 'easybet-net':
    case 'easybet-predictions':
      return 'support@easybet.net';
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-net':
      return 'support@matchbook.com';
    case 'matchbook-mx':
    case 'matchbook-predictions-mexico':
      return 'soporte@matchbook.mx';
    case 'matchbook-erik':
      return 'support@erik.matchbook.com';
    case 'wagyr-io':
      return 'support@wagyr.io';
    case 'blueprint-net':
      return 'support@blueprint.net';
    default:
      return null;
  }
};

export const getClientEnabledPhoneNumberPrefixes = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return ['za', '-'];
    case 'easybet-net':
    case 'easybet-predictions':
      return ['gb', 'ie', '-'];
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-net':
    case 'wagyr-io':
      return ['gb', 'ie', '-'];
    case 'matchbook-mx':
    case 'matchbook-predictions-mexico':
      return ['mx', '-'];
    case 'matchbook-erik':
      return ['us', '-'];
    case 'blueprint-net':
      return ['gb', 'ie', 'mx', 'za', '-'];
    default:
      return [];
  }
};

export const getClientIcons = (client = 'easybet', site = 'net', activeLanguage = 'en-GB') => {
  switch (`${client}-${site}`) {
    case 'easybet-net':
    case 'easybet-predictions':
      return {
        SettingsIcon: SettingsIconEasy,
        SearchIcon: SearchIconEasy,
        LanguageSelectorIcon: GbFlagIcon,
      };
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx': {
      const languageSelectorIcon = activeLanguage === 'es-MX' ? MxFlagIcon : GbFlagIcon;
      return {
        SettingsIcon: SettingsIconEasy,
        SearchIcon: SearchIconEasy,
        LanguageSelectorIcon: languageSelectorIcon,
      };
    }
    case 'betmatch-co.za':
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-net':
    case 'matchbook-erik':
    case 'wagyr-io':
      return {
        SettingsIcon: SettingsIconEasy,
        SearchIcon: SearchIconEasy,
        LanguageSelectorIcon: GbFlagIcon,
      };
    case 'blueprint-net':
      return {
        SettingsIcon: noop,
        SearchIcon: noop,
        LanguageSelectorIcon: noop,
      };
    default:
      return {
        SettingsIcon: noop,
        SearchIcon: noop,
        LanguageSelectorIcon: noop,
      };
  }
};

export const getClientOrigin = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return 'betmatch.co.za';
    case 'easybet-net':
    case 'easybet-predictions':
      return 'www.easybet.net';
    case 'matchbook-com':
      return 'www.matchbook.com';
    case 'matchbook-predictions':
      return 'predictstreet.matchbook.com';
    case 'matchbook-mx':
      return 'www.matchbook.mx';
    case 'matchbook-predictions-mexico':
      return 'predictstreet.matchbook.mx';
    case 'matchbook-net':
      return 'www.matchbook.com';
    case 'matchbook-erik':
      return 'erik.matchbook.com';
    case 'wagyr-io':
      return 'www.wagyr.io';
    case 'blueprint-net':
      return 'www.blueprint.com';
    default:
      return null;
  }
};

export const getClientShortName = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return 'bm';
    case 'easybet-net':
    case 'easybet-predictions':
      return 'eb';
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      return 'mb';
    case 'wagyr-io':
      return 'wg';
    case 'blueprint':
      return 'bp';
    default:
      return null;
  }
};

export const getClientSocialLink = (client = 'easybet', site = 'net', platform) => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      switch (platform) {
        case 'fb':
          return 'https://www.facebook.com/betmatchexchange';
        case 'x':
          return 'https://x.com/BetmatchX';
        case 'insta':
          return 'https://www.instagram.com/betmatchexchange/';
        case 'yt':
          return 'https://www.youtube.com/@betmatchexchange';
        default:
          return null;
      }
    case 'easybet-net':
    case 'easybet-predictions':
      switch (platform) {
        case 'fb':
          return 'https://www.facebook.com/easyBetTeam';
        case 'x':
          return 'https://x.com/TeamEasyBet';
        default:
          return null;
      }
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      switch (platform) {
        case 'fb':
          return 'https://www.facebook.com/MatchbookBettingExchange';
        case 'x':
          return 'https://twitter.com/TeamMatchBook';
        default:
          return null;
      }
    case 'wagyr-io':
      switch (platform) {
        case 'fb':
          return 'https://www.facebook.com/';
        case 'x':
          return 'https://twitter.com/';
        default:
          return null;
      }
    case 'blueprint-net':
      switch (platform) {
        case 'fb':
          return 'https://www.facebook.com/';
        case 'x':
          return 'https://x.com/';
        default:
          return null;
      }
    default:
      return null;
  }
};

export const getClientStyledText = (client = 'easybet', site = 'net', props = {}) => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return <StyledBetmatchSpan {...props} />;
    case 'easybet-net':
    case 'easybet-predictions':
      return <StyledEasySpan {...props} />;
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      return <StyledMatchbookSpan {...props} />;
    case 'wagyr-io':
      return <StyledWagyrSpan {...props} />;
    case 'blueprint':
      return <span>Blueprint</span>;
    default:
      return noop;
  }
};

export const getClientString = (client = 'easybet', site = 'net') => {
  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return 'Betmatch';
    case 'easybet-net':
    case 'easybet-predictions':
      return 'easyBet';
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      return 'Matchbook';
    case 'wagyr-io':
      return 'Wagyr';
    case 'blueprint-net':
      return 'Blueprint';
    default:
      return null;
  }
};

/** Section B - functions with dependencies - ordered alphabetically on their own */
export const getClientFamilyIfAny = (client = 'easybet', site = 'net') => {
  const eBStyledSpan = getClientStyledText(client, site, {
    short: true,
    registered: true,
    size: 20,
  });

  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return noop;
    case 'easybet-net':
    case 'easybet-predictions':
      return (
        <a href="https://www.easy.com/" target="_blank" rel="noopener noreferrer">
          Part of the {eBStyledSpan} family of brands
          <br />
        </a>
      );
    case 'blueprint':
      return <span>...</span>;
    case 'matchbook-com':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-mx':
    case 'matchbook-net':
    case 'matchbook-erik':
      return null;
    case 'wagyr-io':
      return noop;
    default:
      return noop;
  }
};

export const getClientLogo = (
  client = 'easybet',
  site = 'net',
  { short = false, forceText = false, wrapperClassName = '' } = {}
) => {
  const clientStyledText = getClientStyledText(client, site);

  switch (`${client}-${site}`) {
    case 'betmatch-co.za':
      return ({ ...props }) => (
        <Fragment>
          {forceText ? (
            clientStyledText
          ) : (
            <Fragment>
              {short ? (
                <BetmatchLogoLight {...props} />
              ) : (
                <div className={wrapperClassName} style={{ padding: '0 24px' }}>
                  <BetmatchLogoDark {...props} />
                </div>
              )}
            </Fragment>
          )}
        </Fragment>
      );
    case 'easybet-net':
    case 'easybet-predictions':
      return ({ ...props }) => (
        <Fragment>
          {forceText ? (
            clientStyledText
          ) : (
            <div className={wrapperClassName}>
              {short ? (
                <EasyLogoShort {...props} />
              ) : (
                <EasyLogo {...props} style={{ marginLeft: 24 }} />
              )}
            </div>
          )}
        </Fragment>
      );
    case 'matchbook-com':
    case 'matchbook-mx':
    case 'matchbook-predictions':
    case 'matchbook-predictions-mexico':
    case 'matchbook-net':
    case 'matchbook-erik':
      return ({ ...props }) => (
        <Fragment>
          {forceText ? (
            clientStyledText
          ) : (
            <div className={wrapperClassName}>
              {short ? <MatchbookLogo {...props} /> : <MatchbookLogoName {...props} />}
            </div>
          )}
        </Fragment>
      );
    case 'wagyr-io':
      return ({ ...props }) => (
        <Fragment>
          {forceText ? (
            clientStyledText
          ) : (
            <div
              className={wrapperClassName}
              style={{ backgroundColor: '#414445', padding: '0 24px' }}
            >
              <img src="/assets/images/logos/wagyr-logo.png" alt="logo" {...props} />
            </div>
          )}
        </Fragment>
      );
    case 'blueprint':
      return noop;
    default:
      return () => <Fragment />;
  }
};

export default getClientEmail;
